/**
 * BundleDetailModal - Modal showing full bundle details
 *
 * Displays all NFTs in the bundle with links to their detail pages.
 * Provides buy button for buyers and cancel button for owners.
 */

import React, { useState } from 'react';
import { Dialog } from '../../ui/components/Dialog';
import { Button } from '../../ui/components/Button';
import { Avatar } from '../../ui/components/Avatar';
import { Badge } from '../../ui/components/Badge';
import { FeatherX, FeatherExternalLink, FeatherShare2, FeatherCheck } from '@subframe/core';
import { RarityRank } from '../nft/RarityRank';
import { formatPrice } from '../../utils/formatters';
import type { NFTBundle, BundleItem } from '../../api/bundles';

interface BundleDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  bundle: NFTBundle;
  onBuy?: () => void;
  onCancel?: () => void;
  canBuy?: boolean;
  isOwner?: boolean;
}

export const BundleDetailModal: React.FC<BundleDetailModalProps> = ({
  isOpen,
  onClose,
  bundle,
  onBuy,
  onCancel,
  canBuy = false,
  isOwner = false,
}) => {
  const itemCount = bundle.itemCount || bundle.items?.length || 0;
  const items = bundle.items || [];
  const [copied, setCopied] = useState(false);

  const handleNFTClick = (nftTokenId: string) => {
    window.open(`/nfts/${nftTokenId}`, '_blank');
  };

  const handleShare = async () => {
    // Build the share URL with bundle param
    const url = new URL(window.location.href);
    url.searchParams.set('bundle', bundle.id);

    try {
      await navigator.clipboard.writeText(url.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      console.error('Failed to copy to clipboard');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content className="w-full max-w-[560px]">
        <div className="flex w-full flex-col items-start gap-6 p-6">
          {/* Header */}
          <div className="flex w-full items-start justify-between">
            <div className="flex flex-col gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Bundle Details
              </span>
              <span className="text-body text-subtext-color">
                {bundle.collectionName || 'Collection'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={handleShare}
                className="flex h-8 w-8 items-center justify-center rounded hover:bg-neutral-100 relative"
                title="Copy link to bundle"
              >
                {copied ? (
                  <FeatherCheck className="h-5 w-5 text-success-600" />
                ) : (
                  <FeatherShare2 className="h-5 w-5 text-subtext-color" />
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex h-8 w-8 items-center justify-center rounded hover:bg-neutral-100"
              >
                <FeatherX className="h-5 w-5 text-subtext-color" />
              </button>
            </div>
          </div>

          {/* Price Summary */}
          <div className="w-full bg-neutral-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-caption text-subtext-color">Bundle Price</span>
                <span className="text-heading-2 font-heading-2 text-brand-600">
                  {formatPrice(bundle.priceXrp)}
                </span>
              </div>
              <div className="flex flex-col items-end">
                <span className="text-caption text-subtext-color">{itemCount} NFTs</span>
                <span className="text-body text-default-font">
                  {formatPrice(bundle.priceXrp / itemCount)} each
                </span>
              </div>
            </div>

            {/* Seller info */}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-neutral-200">
              <span className="text-caption text-subtext-color">
                Seller: {bundle.sellerAddress.slice(0, 8)}...{bundle.sellerAddress.slice(-6)}
              </span>
              {isOwner && (
                <Badge variant="neutral">You</Badge>
              )}
            </div>
          </div>

          {/* NFT List */}
          <div className="w-full">
            <span className="text-body-bold text-default-font mb-3 block">
              NFTs in this Bundle ({itemCount})
            </span>

            <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
              {items.map((item, index) => (
                <button
                  key={item.nftTokenId}
                  type="button"
                  onClick={() => handleNFTClick(item.nftTokenId)}
                  className="flex items-center gap-3 p-3 rounded-lg border border-neutral-border hover:border-brand-300 hover:bg-neutral-50 transition-colors text-left"
                >
                  <Avatar
                    size="medium"
                    image={item.nft_thumbnail_url || item.nft_image_url}
                  >
                    {item.nft_name?.charAt(0) || '?'}
                  </Avatar>

                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-body-bold text-default-font truncate">
                      {item.nft_name || `NFT #${index + 1}`}
                    </span>
                    <div className="flex items-center gap-2 text-caption text-subtext-color">
                      {item.rarity_rank && (
                        <span className="text-brand-600">
                          Rank #{item.rarity_rank}
                        </span>
                      )}
                      <span className="truncate">
                        {item.nftTokenId.slice(0, 8)}...{item.nftTokenId.slice(-6)}
                      </span>
                    </div>
                  </div>

                  <FeatherExternalLink className="h-4 w-4 text-subtext-color flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>

          {/* Status Badge */}
          {bundle.status !== 'live' && (
            <Badge
              variant={
                bundle.status === 'sold' ? 'success' :
                bundle.status === 'cancelled' ? 'error' :
                bundle.status === 'expired' ? 'warning' :
                'neutral'
              }
            >
              {bundle.status === 'sold' ? 'Sold' :
               bundle.status === 'cancelled' ? 'Cancelled' :
               bundle.status === 'expired' ? 'Expired' :
               bundle.status === 'pending' ? 'Pending' :
               bundle.status}
            </Badge>
          )}

          {/* Footer */}
          <div className="flex w-full items-center justify-end gap-3">
            <Button variant="neutral-secondary" onClick={onClose}>
              Close
            </Button>

            {isOwner && bundle.status === 'live' && onCancel && (
              <Button variant="destructive-secondary" onClick={onCancel}>
                Cancel Bundle
              </Button>
            )}

            {canBuy && !isOwner && bundle.status === 'live' && (
              <Button variant="brand-primary" onClick={onBuy}>
                Buy Bundle - {formatPrice(bundle.priceXrp)}
              </Button>
            )}
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};

export default BundleDetailModal;
